import type { LocationLabelIndex, SuggestionItem } from '@optionway/models';

// Labels are enrichment on top of the BFF-provided ones (enrichItem falls back field by field):
// an unavailable BFF degrades to an empty index — it must never reject, an SSR-blocking query
// that rejects fails the whole document render with a 500. The fallback reports itself through
// onDegraded so the degraded render stays out of the caches.
interface LabelQueryInput {
  apiUrl: string;
  onDegraded?: () => void;
}
export const locationLabelOptions = defineQueryOptions((input: LabelQueryInput & { locale: string }) => ({
  key: ['location-label-index', input.locale],
  query: () =>
    $fetch<LocationLabelIndex>(`${input.apiUrl}/search/labels/${input.locale}`).catch(() => {
      input.onDegraded?.();
      return emptyIndex();
    }),
  staleTime: Infinity,
  enabled: !!input.apiUrl,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
}));

export const airportLabelOptions = defineQueryOptions((input: LabelQueryInput) => ({
  key: ['airport-label-index'],
  query: () =>
    $fetch<LocationLabelIndex>(`${input.apiUrl}/search/labels`).catch(() => {
      input.onDegraded?.();
      return emptyIndex();
    }),
  staleTime: Infinity,
  enabled: !!input.apiUrl,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
}));

const useLabelIndexes = defineQuery(() => {
  const { locale } = useI18n();
  const { $appConfig } = useNuxtApp();
  const apiUrl = $appConfig.api?.url ?? '';
  // Captured at setup (undefined on the client): the SSR fallbacks mark the render degraded so
  // it stays a cache miss (server/plugins/09.degraded-render-cache.ts).
  const requestEvent = useRequestEvent();
  const onDegraded = () => {
    markRenderDegraded(requestEvent);
  };

  const { data: labelData, refresh: refreshLabels } = useQuery(() =>
    locationLabelOptions({ locale: locale.value, apiUrl, onDegraded }),
  );
  const { data: airportData, refresh: refreshAirports } = useQuery(() => airportLabelOptions({ apiUrl, onDegraded }));

  // An absent index degrades the render, which the caches must then not replay; a missing entry does not.
  const fromIndex = (index: LocationLabelIndex | undefined, field: keyof LocationLabelIndex, code?: string) => {
    if (!code) {
      return '';
    }
    if (!index) {
      onDegraded();
      return code;
    }
    return index[field]?.[code] ?? code;
  };

  return {
    city: (code?: string) => fromIndex(labelData.value, 'cityIndex', code),
    country: (code?: string) => fromIndex(labelData.value, 'countryIndex', code),
    airport: (code?: string) => fromIndex(airportData.value, 'airportIndex', code),
    enrichSuggestion: (item?: SuggestionItem): SuggestionItem | undefined =>
      item ? enrichItem(item, labelData.value, airportData.value) : undefined,
    enrichSuggestions: (items?: SuggestionItem[]): SuggestionItem[] | undefined =>
      items?.map((s) => enrichItem(s, labelData.value, airportData.value)),
    awaitIndexes: (): Promise<unknown> =>
      apiUrl ? Promise.all([refreshLabels(), refreshAirports()]) : Promise.resolve(),
  };
});

// defineQuery registers its SSR wait on the first caller only: every reader holds its own.
export const useLocationLabels = () => {
  const labels = useLabelIndexes();
  onServerPrefetch(labels.awaitIndexes);
  return labels;
};

function emptyIndex(): LocationLabelIndex {
  return {};
}

function enrichItem(
  suggestion: SuggestionItem,
  labelIndex?: LocationLabelIndex,
  airportIndex?: LocationLabelIndex,
): SuggestionItem {
  const cityCode = suggestion.cityCode ?? suggestion.code;
  // the index stays first so labels follow a locale change; the BFF label covers the index not being loaded
  return {
    ...suggestion,
    cityLabel: cityCode ? (labelIndex?.cityIndex?.[cityCode] ?? suggestion.cityLabel ?? cityCode) : undefined,
    countryLabel: suggestion.countryCode
      ? (labelIndex?.countryIndex?.[suggestion.countryCode] ?? suggestion.countryLabel ?? suggestion.countryCode)
      : undefined,
    airportLabel: suggestion.code
      ? (airportIndex?.airportIndex?.[suggestion.code] ?? suggestion.airportLabel ?? suggestion.code)
      : undefined,
  };
}
